// 게임 화면 좌표계(가로 고정). 던전히어로는 가로 전용 게임이다(대표님 2026-10-22 c014).
// 터치 기기에서 가로 고정(screen.orientation.lock)이 안 되는 곳(iPhone·iPad Safari, 카카오톡·인스타 같은 인앱 브라우저, 전체 화면 거부)에서
// 폰을 세로로 들면 index.html이 #app 전체를 시계 방향 90도 돌려 가로 화면으로 보여 준다(html.dh-land + 세로 방향일 때).
//   돌린 화면: #app의 왼쪽 위(0,0)가 실제 화면 오른쪽 위에 오고, #app의 위쪽 가장자리가 실제 화면 오른쪽 가장자리를 따라간다.
//   그래서 폰을 시계 반대 방향으로 눕히면(위쪽이 왼손 쪽) 똑바로 보인다. 인트로 층의 회전과 같은 방향이다.
// DOM 배치·hit test는 브라우저가 변환을 알아서 처리하지만, clientX/clientY·innerWidth/innerHeight·getBoundingClientRect는 실제 화면 기준이다.
// 그 값을 쓰는 코드는 이 모듈로 게임 화면(#app 안쪽, 가로) 좌표로 바꿔 쓴다. 돌리지 않은 화면에서는 값을 그대로 돌려준다.

export interface ViewPoint { x: number; y: number }
export interface ViewRect { left: number; top: number; right: number; bottom: number; width: number; height: number }

/** 실제 화면 좌표(sx, sy) → 게임 화면 좌표. rotated면 시계 방향 90도 돌린 #app 안쪽 좌표(screenW = 실제 화면 너비) */
export function mapPoint(rotated: boolean, screenW: number, sx: number, sy: number, out: ViewPoint = { x: 0, y: 0 }): ViewPoint {
  if (rotated) { out.x = sy; out.y = screenW - sx; } else { out.x = sx; out.y = sy; }
  return out;
}

/** 게임 화면 좌표 → 실제 화면 좌표(mapPoint의 역함수, 시험·QA용) */
export function unmapPoint(rotated: boolean, screenW: number, x: number, y: number, out: ViewPoint = { x: 0, y: 0 }): ViewPoint {
  if (rotated) { out.x = screenW - y; out.y = x; } else { out.x = x; out.y = y; }
  return out;
}

/** 실제 화면 사각형(getBoundingClientRect) → 게임 화면 사각형. 90도 회전이라 축에 맞춘 사각형은 축에 맞춘 사각형으로 간다 */
export function mapRect(rotated: boolean, screenW: number, r: { left: number; top: number; width: number; height: number }, out: ViewRect = { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 }): ViewRect {
  if (!rotated) { out.left = r.left; out.top = r.top; out.right = r.left + r.width; out.bottom = r.top + r.height; out.width = r.width; out.height = r.height; return out; }
  const left = r.top, top = screenW - (r.left + r.width);
  out.left = left; out.top = top; out.right = left + r.height; out.bottom = top + r.width; out.width = r.height; out.height = r.width;
  return out;
}

/** 게임 화면 크기: 돌린 화면이면 실제 화면의 가로·세로를 바꾼다 */
export function viewSize(rotated: boolean, innerW: number, innerH: number): { w: number; h: number } {
  return rotated ? { w: innerH, h: innerW } : { w: innerW, h: innerH };
}

// ── 실행 중 상태(브라우저) ──────────────────────────────────────────────
// index.html의 CSS 규칙(`@media (orientation: portrait) { html.dh-land #app {...} }`)과 같은 조건을 같은 미디어 쿼리로 읽어 둘이 어긋나지 않게 한다.
let portraitQuery: MediaQueryList | null | undefined;
const portrait = (): boolean => {
  if (portraitQuery === undefined) portraitQuery = typeof matchMedia === 'function' ? matchMedia('(orientation: portrait)') : null;
  return portraitQuery ? portraitQuery.matches : false;
};

/** 지금 #app이 90도 돌아가 있는가 */
export function viewRotated(): boolean {
  if (typeof document === 'undefined' || !document.documentElement) return false;
  return document.documentElement.classList.contains('dh-land') && portrait();
}

/** 게임 화면 너비(CSS px). window.innerWidth 대신 쓴다 */
export function viewW(): number {
  if (typeof window === 'undefined') return 0;
  return viewRotated() ? window.innerHeight : window.innerWidth;
}

/** 게임 화면 높이(CSS px). window.innerHeight 대신 쓴다 */
export function viewH(): number {
  if (typeof window === 'undefined') return 0;
  return viewRotated() ? window.innerWidth : window.innerHeight;
}

const shared: ViewPoint = { x: 0, y: 0 };
/** 포인터 이벤트 clientX/clientY → 게임 화면 좌표. 돌려받은 객체는 다음 호출에서 덮어쓴다(프레임 안 할당 없음) */
export function toView(clientX: number, clientY: number, out: ViewPoint = shared): ViewPoint {
  return mapPoint(viewRotated(), typeof window === 'undefined' ? 0 : window.innerWidth, clientX, clientY, out);
}

/** 요소의 화면 사각형을 게임 화면 좌표로(getBoundingClientRect 대신 쓴다). out을 주면 그 객체를 덮어써 돌려준다(프레임 루프용, 새 객체 없음) */
export function viewRect(el: Element, out?: ViewRect): ViewRect {
  return mapRect(viewRotated(), typeof window === 'undefined' ? 0 : window.innerWidth, el.getBoundingClientRect(), out);
}
